import React, { useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Grid, Button, useTheme, alpha } from '@mui/material';
import SectionContainer from './SectionContainer';
import AnimatedElement from './AnimatedElement';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const faqItems = [
  {
    question: 'Как начать использовать сервис?',
    answer: 'Для начала работы достаточно найти нашего бота в Telegram по имени @webigram_bot и отправить ему команду /start. Далее следуйте инструкциям для регистрации и настройки вашего первого вебинара.',
  },
  {
    question: 'Как интегрировать YouTube-трансляцию?',
    answer: 'При создании вебинара вам необходимо указать ссылку на YouTube-трансляцию в соответствующем поле. Система автоматически интегрирует трансляцию в интерфейс вебинара для зрителей.',
  },
  {
    question: 'Сколько слайдов я могу загрузить для презентации?',
    answer: 'В базовом тарифе вы можете загрузить до 50 слайдов презентации в формате JPG или PNG. В расширенном тарифе ограничение увеличено до 100 слайдов.',
  },
  {
    question: 'Можно ли настроить автоматическую отправку сообщений после вебинара?',
    answer: 'Да, в панели управления вебинаром есть раздел "Автоматизация", где вы можете настроить отправку сообщений участникам после завершения вебинара. Сообщения могут включать благодарность за участие, дополнительные материалы или специальные предложения.',
  },
  {
    question: 'Какие данные о зрителях я получу после вебинара?',
    answer: 'После проведения вебинара вы получите доступ к следующим данным: никнеймы участников в Telegram, время их присутствия на вебинаре, взаимодействие с кнопками призыва к действию, а также (при наличии в их аккаунтах) имена и фамилии пользователей.',
  },
  {
    question: 'Можно ли провести бесплатный тестовый вебинар?',
    answer: 'Да, в рамках 14-дневного пробного периода вы можете провести до 3 тестовых вебинаров с ограничением до 50 участников для каждого. Все функции сервиса будут доступны в полном объеме.',
  },
  {
    question: 'Есть ли ограничения по количеству участников?',
    answer: 'Количество участников зависит от выбранного тарифа. В тарифе "Стартовый" доступно до 100 участников, в тарифе "Профессиональный" — до 500 участников, а в тарифе "Корпоративный" количество участников не ограничено.',
  },
  {
    question: 'Как организовать запись на вебинар?',
    answer: 'При создании вебинара система генерирует уникальную ссылку, которую вы можете распространять любым удобным способом. Для записи на вебинар пользователю достаточно перейти по этой ссылке и подтвердить участие в боте.',
  },
];

const FaqSection = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <SectionContainer
      id="faq"
      title="Часто задаваемые вопросы"
      subtitle="Ответы на популярные вопросы о нашем сервисе для проведения вебинаров в Telegram"
    >
      <Grid container spacing={6}>
        <Grid item xs={12} lg={8}>
          <Box>
            {faqItems.map((item, index) => (
              <AnimatedElement key={index} animation="fadeInUp" delay={0.1 * index}>
                <Accordion
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    borderRadius: '8px',
                    border: `1px solid ${alpha(theme.palette.primary.main, expanded === `panel${index}` ? 0.2 : 0.1)}`,
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon 
                        sx={{ 
                          color: expanded === `panel${index}` ? 'primary.main' : 'text.secondary',
                          fontSize: 28
                        }} 
                      />
                    }
                    sx={{
                      backgroundColor: expanded === `panel${index}` ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Typography variant="h6" fontWeight={expanded === `panel${index}` ? 'bold' : 'medium'}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" color="text.secondary">
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </AnimatedElement>
            ))}
          </Box>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <AnimatedElement animation="fadeIn" delay={0.4}>
            <Box
              sx={{
                p: 4,
                borderRadius: 4,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  mx: 'auto',
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 40 }} />
              </Box>
              
              <Typography variant="h5" textAlign="center" fontWeight="bold" gutterBottom>
                Не нашли ответ на свой вопрос?
              </Typography>
              
              <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
                Наша команда поддержки готова помочь вам в любое время. Свяжитесь с нами, и мы ответим на все ваши вопросы.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                sx={{
                  mx: 'auto',
                  px: 4,
                  borderRadius: 2,
                }}
              >
                Связаться с поддержкой
              </Button>
            </Box>
          </AnimatedElement>
        </Grid>
      </Grid>
    </SectionContainer>
  );
};

export default FaqSection; 